/*
 * Copyright (c) 2026 Bluemate B.V.
 *
 * The live Android install banner, reused as the final step of the Mobile App
 * install guide. Mirrors the Android banners in pwaBanners.tsx: a Chromium
 * browser that captured `beforeinstallprompt` gets the one-tap "Install" banner;
 * an incompatible Android browser (Samsung Internet / Firefox) gets an
 * "open in Chrome" banner. Renders nothing when neither applies.
 */

import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import AndroidInstallPromptBanner from './androidInstallPromptBanner';
import {onShowConfirmSnackbar} from '../../util/renderUtil';
import {
    hasInstallPrompt,
    isAndroid,
    isFirefox,
    isIOS,
    isMacSafari,
    isPWAInstalled,
    isSamsungBrowser,
    openInChrome,
    promptInstall,
    PWA_INSTALL_AVAILABLE_EVENT
} from '../../util/pwaUtil';

/** Which banner, if any, this browser can show right now. Exported so callers
 *  that frame the banner (the Mobile App guide numbers it as a step) can gate
 *  themselves on the SAME condition instead of a second guess — a step saying
 *  "follow the banner below" above nothing is worse than no step at all. */
export type AndroidInstallBannerMode = 'install' | 'open-in-chrome' | null;

export function getAndroidInstallBannerMode(): AndroidInstallBannerMode {
    if (hasInstallPrompt() && !isPWAInstalled() && !isIOS() && !isMacSafari() && !isFirefox()) return 'install';
    if (isAndroid() && !hasInstallPrompt() && (isSamsungBrowser() || isFirefox())) return 'open-in-chrome';
    return null;
}

/** Live version of {@link getAndroidInstallBannerMode}: re-evaluates whenever the
 *  install state changes (prompt captured, prompt spent, app installed). */
export function useAndroidInstallBannerMode(): AndroidInstallBannerMode {
    const [mode, setMode] = useState<AndroidInstallBannerMode>(getAndroidInstallBannerMode);

    useEffect(() => {
        const evaluate = () => setMode(getAndroidInstallBannerMode());
        evaluate();
        window.addEventListener(PWA_INSTALL_AVAILABLE_EVENT, evaluate);
        window.addEventListener('appinstalled', evaluate);
        return () => {
            window.removeEventListener(PWA_INSTALL_AVAILABLE_EVENT, evaluate);
            window.removeEventListener('appinstalled', evaluate);
        };
    }, []);

    return mode;
}

export default function AndroidInstallBanner() {
    const {t} = useTranslation();
    const mode = useAndroidInstallBannerMode();
    const [installLoading, setInstallLoading] = useState(false);

    function showError() {
        onShowConfirmSnackbar(t('pwa.actionError', 'Er ging iets mis. Probeer het opnieuw.'), {
            backgroundColor: 'color-red-warning',
            textColor: 'white'
        });
    }

    async function onClickInstall() {
        if (installLoading) return;
        setInstallLoading(true);
        try {
            const result = await promptInstall();
            if (result === 'unavailable') showError();
        } catch {
            showError();
        } finally {
            setInstallLoading(false);
        }
    }

    // Chromium browser with a captured install prompt → one-tap install.
    if (mode === 'install')
        return (
            <AndroidInstallPromptBanner variant='install' onAction={onClickInstall} actionLoading={installLoading} />
        );

    // Incompatible Android browser (Samsung Internet / Firefox) → open in Chrome.
    if (mode === 'open-in-chrome')
        return <AndroidInstallPromptBanner variant='open-in-chrome' onAction={openInChrome} />;

    return null;
}
